import { useCallback, useMemo, useState } from "react";
import type { Dp, Filter } from "./ExState";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import { IconButton, Switch, InputLabel } from "@mui/material";
import OpenInFull from "@mui/icons-material/OpenInFull";
import UnitImage from "common/src/tft-static/UnitImage";
import { make as UnitName0 } from "common/src/tft-static/UnitName0.bs";
import ExStarUnits from "./ExStarUnits";
import { make as SearchField } from "../components/SearchField2.bs";
import { searchUnits } from "../common/Search2";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import { use as useSet } from "common/src/data/TftSet.bs";
import { make as UnitTooltip } from "common/src/tft-static/UnitTooltip.bs";
import { common as t } from "common/src/bindings/Translation2";

const cols = [
  makeCol({
    width: 180,
    order: ([unitId], _, { unitsT }) => unitsT(unitId),
    defaultDesc: false,
    renderTitle: (_, { t }) => <> {t("Unit")} </>,
    renderValue: ([unitId, _]) => (
      <div className="flex items-center text-sm">
        <div className="flex-shrink-0">
          <UnitTooltip unit={unitId}>
            <UnitImage unitId={unitId} size={28} showName={false} />
          </UnitTooltip>
        </div>
        <div className="pl-[6px] font-roboto font-normal truncate">
          <UnitName0 unit={unitId} />
        </div>
      </div>
    ),
  }),
  makeCol({
    width: 90,
    order: ([_, v]) => v.count,
    alignRight: true,
    // renderTitle: () => <> Games </>,
    renderTitle: (_, { t }) => <> {t("Games")} </>,
    renderValue: ([_, v]) => <> {humanReadableCount(v.count)} </>,
  }),
  makeCol({
    width: 90,
    order: ([_, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Play rate")} </>,
    tooltip: ([_, v]) => <> {humanReadableCount(v.count) + " games"} </>,
    renderValue: ([_, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: ([_, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Place")} </>,
    renderValue: ([_, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : (v.place / v.count).toFixed(2)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: ([_, v], { hidePlacements }) => (hidePlacements ? 0 : v.delta),
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => (
      <Tooltip
        title={
          "Delta shows the difference in average placement of the selected filter combination with the unit compared to the average placement of the filter combination without the unit. Lower is better."
        }
      >
        <div>{t("Delta")}</div>
      </Tooltip>
    ),

    renderValue: ([_, v], { hidePlacements }) => {
      return v.delta && !hidePlacements ? (
        <> {(v.delta > 0 ? "+" : "") + v.delta.toFixed(2)} </>
      ) : (
        <>—</>
      );
    },
  }),
  makeCol({
    width: 90,
    order: ([_, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.top4 / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Top 4")}% </>,
    renderValue: ([_, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : formatPct2(v.top4 / v.count)} </>
    ),
  }),
  makeCol({
    width: 80,
    order: ([_, v], { hidePlacements }) =>
      hidePlacements ? 0 : v.won / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Win")}% </>,
    renderValue: ([_, v], { hidePlacements }) => (
      <> {hidePlacements ? "—" : formatPct2(v.won / v.count)} </>
    ),
  }),
  makeCol({
    width: 48,
    alignRight: true,
    renderTitle: (_) => <div className="h-1" />,
    valueClass: "!justify-start",
    renderValue: ([unitId], { onExpand, hideExpand }, { unitsT }) => {
      return hideExpand ? (
        <div />
      ) : (
        <>
          <IconButton
            onClick={(_) =>
              onExpand({
                typ: "u",
                id: unitId,
                sid: unitId,
                starLevel: 0,
                label: unitsT(unitId),
              })
            }
            classes={{
              root: "text-lg",
            }}
          >
            <OpenInFull fontSize="inherit" />
          </IconButton>
        </>
      );
    },
  }),
];

type Props = {
  data: Array<[string, Dp]>;
  starData: Array<[string, number, Dp]>;
  baseCount: number;
  onExpand: (f: Filter) => void;
  hideExpand: boolean;
  minSample: number;
  hidePlacements: boolean;
};

const ExUnits = ({
  data,
  starData,
  baseCount,
  onExpand,
  hideExpand,
  minSample,
  hidePlacements,
}: Props) => {
  const set = useSet();
  const [star, setStar] = useState(false);
  const [search, setSearch] = useState("");
  const keyFn = useCallback(([id, _]: [string, Dp]) => id, []);
  const context = useMemo(
    () => ({ baseCount, onExpand, hideExpand, hidePlacements }),
    [baseCount, onExpand, hideExpand, hidePlacements],
  );

  const filteredData = useMemo(
    () => data.filter(([_, v]) => v.count >= minSample),
    [data, minSample],
  );

  const visibleFilter = useMemo(() => {
    if (search === "") {
      return () => true;
    }
    const passIds = searchUnits(search, set);

    return ([unitId]: [string]) => passIds.has(unitId);
  }, [search]);

  return (
    <div>
      <div className="flex items-center p-2 cursor-pointer select-none">
        <div className="pr-5">
          <SearchField search={search} setSearch={setSearch} />
        </div>
        <Switch
          id="star-switch"
          checked={star}
          onChange={(e) => setStar(e.target.checked)}
          size="small"
        />
        <InputLabel htmlFor="star-switch">
          {t("Split by star level")}
        </InputLabel>
      </div>
      {star ? (
        <ExStarUnits
          data={starData}
          baseCount={baseCount}
          onExpand={onExpand}
          search={search}
          hideExpand={hideExpand}
          minSample={minSample}
          hidePlacements={hidePlacements}
        />
      ) : data.length === 0 ? (
        <div className="px-5 py-6 text-xl text-medium text-white2">
          No unit combos with at least 100 games played
        </div>
      ) : (
        <StatsTable
          cols={cols}
          data={filteredData}
          keyFn={keyFn}
          stickyColCount={1}
          defaultSortIdx={1}
          top="t47m92"
          context={context}
          visibleFilter={visibleFilter}
        />
      )}
    </div>
  );
};

export default ExUnits;
